import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { patternFields } from '../_shared/pattern.js';
import { headingBlockSchema } from '../../blocks/heading/index.js';
import { textBlockSchema } from '../../blocks/text/index.js';
import { buttonBlockSchema } from '../../blocks/button/index.js';
import { dividerBlockSchema } from '../../blocks/divider/index.js';
import { ratingBlockSchema } from '../../blocks/rating/index.js';
import { imageBlockSchema } from '../../blocks/image/index.js';
/** Shrine "Display on" visibility (mirrors the block-level field) — All devices / Mobile only / Desktop only. */
const VISIBILITY_OPTIONS = [
    { value: 'always-display', label: 'All devices' },
    { value: 'desktop-hidden', label: 'Mobile only' },
    { value: 'mobile-hidden', label: 'Desktop only' },
];
const CONTENT_ALIGN_OPTIONS = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];
/**
 * rich-text — Shrine's COMPOSABLE rich-text section. It is now a CONTAINER: instead of one monolithic
 * `content` richtext field, it holds `heading` / `text` / `button` / `divider` / `rating` / `image` CHILD
 * BLOCKS (the same reusable blocks Shrine's rich-text accepts). Each block owns its full Shrine config; the
 * section keeps only the band layout (colour scheme, padding, width + content position / full-width).
 *
 * v2 → v3: the model changed from a single richtext field to a block container. Old `content`/`align`/
 * `spacing` keys are harmless leftovers (ignored by the container Component); a fresh instance is seeded
 * from the preset's heading + text + button blocks.
 *
 * v3 → v4: the 740px prose column is no longer the silent default. Each text/heading block now owns a
 * "Desktop width" (% of the section), and a hard column cap made « 100 % » render as barely half the
 * section (founder report, 2026-09-11). The cap is now an OPT-IN checkbox `narrowColumn`; the old
 * `fullWidth` flag is folded into it (narrowColumn = !fullWidth) so every stored section keeps its look.
 */
const RICH_TEXT_ACCEPTS = ['heading', 'text', 'button', 'divider', 'rating', 'image'];
export const richTextSchema = {
    type: 'rich-text',
    name: 'Rich text',
    version: 4,
    templates: ['index', 'collection', 'page', 'product', 'thank-you'],
    accepts: [...RICH_TEXT_ACCEPTS],
    // The block SCHEMAS the container can instantiate (the same global blocks listed in `accepts`). Required
    // so the recursive instantiator can seed the preset's children + the builder can add them.
    blocks: [
        headingBlockSchema,
        textBlockSchema,
        buttonBlockSchema,
        dividerBlockSchema,
        ratingBlockSchema,
        imageBlockSchema,
    ],
    maxBlocks: 20,
    fields: [
        // ── Top-level (Shrine display_id + visibility). ──────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            info: 'Adds an anchor id to this section so a button link can scroll to it.',
            default: false,
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: VISIBILITY_OPTIONS,
        },
        // ── Layout (Shrine content position + alignment, full / narrow widths). ───────────────────────────
        {
            id: 'desktopContentPosition',
            type: 'select',
            label: 'Desktop content position',
            group: 'Layout',
            info: 'Position is automatically optimized for mobile.',
            default: 'center',
            options: CONTENT_ALIGN_OPTIONS,
        },
        {
            id: 'desktopContentAlignment',
            type: 'select',
            label: 'Desktop content alignment',
            group: 'Layout',
            default: 'center',
            options: CONTENT_ALIGN_OPTIONS,
        },
        {
            id: 'mobileContentAlignment',
            type: 'select',
            label: 'Mobile content alignment',
            group: 'Layout',
            default: 'center',
            options: CONTENT_ALIGN_OPTIONS,
        },
        {
            id: 'narrowColumn',
            type: 'checkbox',
            label: 'Limit content to a narrow column',
            group: 'Layout',
            info: 'Caps the content at 740px on desktop. Leave it off so each block\'s "Desktop width" is a share of the whole section.',
            default: false,
        },
        {
            id: 'narrowMobileWidth',
            type: 'checkbox',
            label: 'Enable narrow mobile page width',
            group: 'Layout',
            default: false,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal) — colorScheme / padding / width. ──────────────────────────────────────────
        ...universalFields,
        // ── Background pattern (shared Shrine engine). ───────────────────────────────────────────────────
        ...patternFields(),
    ],
    migrate(fromVersion, settings) {
        // v1/v2 were a monolithic `content` richtext field; v3 is a block container. Settings pass through
        // (the container Component ignores the old keys); blocks are seeded from the preset on fresh instances.
        // v3 → v4: the 740px column becomes opt-in. A stored section keeps it unless it had asked for full width.
        // IDEMPOTENT: hydrateDocument migrates settings but leaves the stored `version` alone, so this runs
        // again on every render (builder canvas, storefront). A `narrowColumn` the seller has since set wins.
        if (fromVersion >= 4)
            return settings;
        const { fullWidth, narrowColumn, ...rest } = settings;
        return {
            ...rest,
            narrowColumn: typeof narrowColumn === 'boolean' ? narrowColumn : fullWidth !== true,
        };
    },
};
export const richTextPresets = [
    {
        name: 'Rich text',
        settings: {
            desktopContentPosition: 'center',
            colorScheme: 'light',
            paddingTop: 64,
            paddingBottom: 64,
            width: 'wide',
        },
        // Shrine's default rich-text content: a heading + a paragraph + a button, each a real child block.
        blocks: [
            {
                type: 'heading',
                settings: {
                    title: { format: 'html', html: 'Pourquoi nous' },
                    headingSize: 'h1',
                    alignment: 'center',
                    mobileAlignment: 'mobile-center',
                },
            },
            {
                type: 'text',
                settings: {
                    text: {
                        format: 'html',
                        html: '<p>Qualité, prix justes, et un service qui répond. Racontez ici ce qui rend votre marque unique.</p>',
                    },
                    alignment: 'center',
                    mobileAlignment: 'center',
                    desktopWidthPx: 480,
                },
            },
            {
                type: 'button',
                settings: {
                    button_label: '@t:common.learn_more',
                    alignment: 'center',
                    mobile_alignment: 'center',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const richTextMeta = {
    label: 'Rich text',
    group: 'Content',
    icon: 'type',
};
//# sourceMappingURL=schema.js.map